import React from 'react'
import PropTypes from 'prop-types'
import styled, { useTheme } from 'styled-components'
import { motion } from 'framer-motion'
import { Tonality } from '@dsny/dsny-component-library'
import { Arrowback, Search, Sparkles } from '@dsny/dsny-component-library/icons'
import { BackButton } from './ReleasesMenu.styles'
import { useSearchTracking } from '../../containers/Search/hooks/useSearchTracking'
import {
  SEARCH_TRACKING_EVENTS,
  SEARCH_ACTIONS,
  SEARCH_PLATFORMS
} from '../../constants/searchTracking'

const Row = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 4px;
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width};
`

function ReleasesSearchBar({
  inputRef,
  isMobile,
  isSearching,
  isSearchRoute,
  value,
  placeholder,
  onChange,
  onFocus,
  onEnter,
  onBack
}) {
  const theme = useTheme()
  const { trackSearchEvent } = useSearchTracking()
  const platform = isMobile ? SEARCH_PLATFORMS.MOBILE : SEARCH_PLATFORMS.DESKTOP

  const isInSearchModeOrRoute = isSearchRoute || isSearching
  const containerWidth = isInSearchModeOrRoute ? (isMobile ? '100%' : 400) : 190

  const getTailingIcon = (condition = 'idle') => {
    switch (condition) {
      case 'search':
        return (
          <Search
            color={theme.colors.neutralW100}
            width={16}
            height={16}
            style={{
              transform: 'translateY(2px)'
            }}
          />
        )

      case 'idle':
        return (
          <Sparkles
            color={theme.colors.player50}
            style={{
              transform: 'translateY(20%)',
              filter: 'drop-shadow(0 0 4px #617EFF)'
            }}
          />
        )

      case 'default':
        return null
    }
  }

  return (
    <Row
      $width={containerWidth}
      layout
      transition={{ layout: { type: 'spring', bounce: 0.32, duration: 0.35 } }}
    >
      {(isSearchRoute || isSearching) && (
        <BackButton type="button" aria-label="Back" onClick={onBack}>
          <Arrowback width={18} height={18} color={theme.colors.neutralW90} />
        </BackButton>
      )}
      <div data-cy="search-input" style={{ width: '100%' }}>
        <Tonality.TextField
          setting="player"
          ref={inputRef}
          value={value}
          labelText=""
          className="releases-search-bar"
          wrapperStyles={{
            width: '100%',
            marginBottom: 0,
            paddingRight: 0
          }}
          inputStyle={{
            height: 44,
            fontSize: 14,
            cursor: 'pointer',
            border: `1px solid ${theme.colors.neutralW50}`
          }}
          placeholder={placeholder}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={onFocus}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              trackSearchEvent?.({
                event: SEARCH_TRACKING_EVENTS.SEARCH_SUBMIT,
                action: SEARCH_ACTIONS.SUBMIT,
                label: value ?? '',
                platform,
                source: 'releases_menu',
                type: 'search',
                search_query_length: (value || '').length,
                has_query: String(Boolean(value?.trim())),
                is_search_route: String(Boolean(isSearchRoute))
              })
              onEnter?.()
            }
          }}
          leadingIconComponent={
            !isInSearchModeOrRoute && (
              <Search
                color={theme.colors.player50}
                width={16}
                height={16}
                style={{
                  transform: 'translateY(2px)',
                  filter: 'drop-shadow(0 0 4px #617EFF)'
                }}
              />
            )
          }
          tailingIconComponent={getTailingIcon(
            isInSearchModeOrRoute ? 'search' : 'idle'
          )}
        />
      </div>
    </Row>
  )
}

ReleasesSearchBar.propTypes = {
  inputRef: PropTypes.object,
  isMobile: PropTypes.bool,
  isSearching: PropTypes.bool,
  isSearchRoute: PropTypes.bool,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onEnter: PropTypes.func,
  onBack: PropTypes.func
}

export default ReleasesSearchBar
